import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useGameConfig } from '../../context/GameConfigContext';
import { ItemImage } from '../inventory/inventoryBagGridUtils';

const WikiItemPopup = ({ item, itemType, efsunProfiles, onClose }) => {
  const { t } = useTranslation('wiki');
  const { gelistirmeConsole: showChances } = useGameConfig();
  const [dropSources, setDropSources] = useState([]);
  const [dropSourcesLoading, setDropSourcesLoading] = useState(false);
  const [dropSourcesError, setDropSourcesError] = useState('');

  const efsunForProfile = useMemo(() => {
    if (!item?.efsunProfile || !efsunProfiles) return null;
    return efsunProfiles.find((p) => p.profileName === item.efsunProfile) || null;
  }, [item, efsunProfiles]);

  useEffect(() => {
    if (!item?.id || !itemType) {
      setDropSources([]);
      return undefined;
    }

    let cancelled = false;
    setDropSourcesLoading(true);
    setDropSourcesError('');
    setDropSources([]);

    axios.get('/api/wiki/item-drops', { params: { type: itemType, id: item.id } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          setDropSources(res.data.sources || []);
        } else {
          setDropSourcesError(t('loadError'));
        }
      })
      .catch(() => {
        if (!cancelled) setDropSourcesError(t('loadError'));
      })
      .finally(() => {
        if (!cancelled) setDropSourcesLoading(false);
      });

    return () => { cancelled = true; };
  }, [item?.id, itemType, t]);

  if (!item) return null;

  const kindLabel = (kind) => ({
    monster: t('kindMonster'),
    metin: t('kindMetin'),
    boss: t('kindBoss'),
  }[kind] || kind);

  return (
    <div className="wiki-popup-backdrop wiki-item-screen" onClick={onClose} role="presentation">
      <div className="wiki-popup wiki-item-popup" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="wiki-popup-sticky-top">
          <button type="button" className="wiki-popup-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="wiki-item-header">
          {item.imageUrl ? (
            <ItemImage imageUrl={item.imageUrl} alt={item.name} className="wiki-item-preview" />
          ) : (
            <span className="wiki-item-preview wiki-item-thumb--placeholder">?</span>
          )}
          <div>
            <h3>{item.name}</h3>
            <p>{t('level')}: {item.level}</p>
            {item.description ? <p className="wiki-item-desc">{item.description}</p> : null}
          </div>
        </div>

        <div className="wiki-popup-section wiki-drop-sources">
          <h4>{t('droppedBy')}</h4>
          {dropSourcesLoading ? <p>{t('loading')}</p> : null}
          {dropSourcesError ? <p className="wiki-error">{dropSourcesError}</p> : null}
          {!dropSourcesLoading && !dropSourcesError && dropSources.length === 0 ? (
            <p className="wiki-empty">{t('noDropSources')}</p>
          ) : null}
          {dropSources.length > 0 ? (
            <div className="wiki-drop-list wiki-drop-source-list">
              {dropSources.map((src, idx) => (
                <div key={`${src.kind}-${src.entityId}-${src.source}-${idx}`} className="wiki-drop-row wiki-drop-source-row">
                  <div className="wiki-drop-info">
                    <span className="wiki-kind-badge wiki-drop-source-kind">{kindLabel(src.kind)}</span>
                    <span className="wiki-drop-name">{src.name}</span>
                    <span className="wiki-drop-rate">
                      {t('level')}: {src.level}
                      {' · '}
                      {t('dropRate')}: %{src.dropPercent?.toFixed(1)} ({src.dropRate}‰)
                      {src.source === 'stomach' ? (
                        <> · {t('dropSourceStomach')}</>
                      ) : null}
                      {src.source === 'core' ? (
                        <> · {t('dropSourceCore')}</>
                      ) : null}
                      {src.countMin > 0 || src.countMax > 0 ? (
                        <> · {t('countRange', { min: src.countMin || 1, max: src.countMax || src.countMin || 1 })}</>
                      ) : null}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <p className="wiki-note">{t('dropRateNote')}</p>
        </div>

        <div className="wiki-enhancement-list">
          {item.enhancements?.map((row) => (
            <div key={row.enhancement} className="wiki-enhancement-row">
              <div className="wiki-enhancement-level">{t('enhancementLevel', { level: row.enhancement })}</div>
              <div className="wiki-enhancement-stats">
                <strong>{t('stats')}:</strong>
                {row.stats?.length > 0 ? (
                  <ul>
                    {row.stats.map((s) => (
                      <li key={s.key}>{s.label}: {s.value}</li>
                    ))}
                  </ul>
                ) : (
                  <span> —</span>
                )}
              </div>
              {row.upgrade ? (
                <div className="wiki-enhancement-upgrade">
                  <strong>{t('upgradeTo', { from: row.upgrade.from, to: row.upgrade.to })}</strong>
                  <div>{t('goldCost')}: {row.upgrade.goldCost?.toLocaleString()}</div>
                  <div>{t('successRate')}: %{row.upgrade.successRate}</div>
                  {row.upgrade.requiredItems?.length > 0 ? (
                    <div className="wiki-materials">
                      <span>{t('requiredMaterials')}:</span>
                      {row.upgrade.requiredItems.map((mat) => (
                        <div key={`${mat.itemType}-${mat.itemId}`} className="wiki-material-row">
                          {mat.imageUrl ? (
                            <ItemImage imageUrl={mat.imageUrl} alt={mat.name} className="wiki-material-thumb" />
                          ) : null}
                          <span>{mat.name} ×{mat.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : row.enhancement < 9 ? (
                <div className="wiki-enhancement-upgrade wiki-enhancement-upgrade--muted">{t('noUpgrade')}</div>
              ) : null}
            </div>
          ))}
        </div>

        {efsunForProfile ? (
          <div className="wiki-efsun-preview">
            <h4>{t('possibleEfsun')}</h4>
            <p>{t('efsunProfile', { profile: efsunForProfile.profileName })}</p>
            {showChances && <p className="wiki-note">
              {t('efsunTypeChance', {
                percent: efsunForProfile.types?.[0]?.typeChancePercent?.toFixed(1) ?? '0',
              })}
            </p>}
            <div className="wiki-efsun-mini-table">
              {efsunForProfile.types?.map((etype) => (
                <div key={etype.typeKey} className="wiki-efsun-mini-row">
                  <span className="wiki-efsun-label">{etype.label}</span>
                  <span className="wiki-efsun-values">
                    {etype.values?.map((v, i) => (
                      <span key={i} className="wiki-efsun-tier-val">T{i + 1}:{v}{etype.percent ? '%' : ''}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WikiItemPopup;
